/**
 * Uploads artwork/photos to the form-uploads worker as they are chosen and keeps the hidden
 * contact[Files] field of the native Shopify contact form in sync with the download links.
 * Markup: snippets/form-uploads.liquid. Worker: workers/form-uploads.
 */
(() => {
  const EXTENSIONS = [
    'jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif', 'tif', 'tiff', 'bmp', 'svg',
    'pdf', 'ai', 'eps', 'psd', 'cdr', 'doc', 'docx', 'xls', 'xlsx', 'csv', 'txt', 'zip',
  ];
  const MAX_MB = 100;
  const MAX_BYTES = MAX_MB * 1024 * 1024;
  const MAX_FILES = 10;
  const SESSION_MARGIN_SECONDS = 60;
  const TOKEN_TIMEOUT_MS = 60000;
  const TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
  const FALLBACK_EMAIL = 'info@waltersofclydebank.co.uk';
  const STORAGE_PREFIX = 'walters:form-uploads:';
  const MENTIONS_ATTACHMENT = /\battach|\benclos/i;

  const uploadForms = [];
  let turnstileLoading = null;

  function loadTurnstile() {
    if (window.turnstile) return Promise.resolve(window.turnstile);
    if (!turnstileLoading) {
      turnstileLoading = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = TURNSTILE_SRC;
        script.async = true;
        script.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error('Turnstile did not initialise')));
        script.onerror = () => {
          turnstileLoading = null;
          reject(new Error('Turnstile could not be loaded'));
        };
        document.head.appendChild(script);
      });
    }
    return turnstileLoading;
  }

  function extensionOf(name) {
    const dot = name.lastIndexOf('.');
    return dot === -1 ? '' : name.slice(dot + 1).toLowerCase();
  }

  function formatSize(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  function expiryOf(url) {
    try {
      return Number(new URL(url).searchParams.get('exp')) || 0;
    } catch {
      return 0;
    }
  }

  function readStored(key) {
    try {
      return JSON.parse(localStorage.getItem(key));
    } catch {
      return null;
    }
  }

  function writeStored(key, value) {
    try {
      if (value === null) localStorage.removeItem(key);
      else localStorage.setItem(key, JSON.stringify(value));
    } catch {}
  }

  const hasFiles = (event) => Array.from(event.dataTransfer ? event.dataTransfer.types : []).includes('Files');

  // a file dropped outside a drop zone would make the browser open it and the form would be lost
  document.addEventListener('dragover', (event) => {
    if (!hasFiles(event)) return;
    event.preventDefault();
    if (!uploadForms.some((form) => form.contains(event.target))) event.dataTransfer.dropEffect = 'none';
  });
  document.addEventListener('drop', (event) => {
    if (hasFiles(event)) event.preventDefault();
  });

  class SessionError extends Error {}

  function init(root) {
    const endpoint = (root.dataset.endpoint || '').replace(/\/+$/, '');
    const siteKey = root.dataset.sitekey;
    const form = root.closest('form');
    const hiddenInput = root.querySelector('[data-form-uploads-field]');
    const zone = root.querySelector('[data-form-uploads-zone]');
    const fileInput = root.querySelector('[data-form-uploads-input]');
    const list = root.querySelector('[data-form-uploads-list]');
    const widgetEl = root.querySelector('[data-form-uploads-widget]');
    const hintEl = root.querySelector('[data-form-uploads-hint]');
    const errorEl = root.querySelector('[data-form-uploads-error]');
    const announcer = root.querySelector('[data-form-uploads-status]');
    if (!endpoint || !siteKey || !form || !hiddenInput || !zone || !fileInput || !list || !widgetEl || !hintEl || !errorEl) return;

    const submitButton = form.querySelector('button[type="submit"], input[type="submit"]');
    const submitLabel = submitButton ? submitButton.textContent : '';
    const storageKey = `${STORAGE_PREFIX}${location.pathname}:${form.id || 'form'}`;
    const items = [];
    let session = null;
    let sessionRequest = null;
    let widgetId = null;
    let pendingToken = null;
    let processing = false;
    let nextId = 1;
    let rejectedFile = false;
    let warned = false;
    let warningLine = null;

    uploadForms.push(form);
    hiddenInput.value = '';

    function announce(message) {
      if (announcer) announcer.textContent = message;
    }

    function clearError() {
      errorEl.replaceChildren();
    }

    function addError(...parts) {
      const line = document.createElement('p');
      line.append(...parts);
      errorEl.appendChild(line);
      return line;
    }

    function emailLink() {
      const link = document.createElement('a');
      link.href = `mailto:${FALLBACK_EMAIL}`;
      link.textContent = FALLBACK_EMAIL;
      return link;
    }

    function resetWarning() {
      warned = false;
      if (warningLine) warningLine.remove();
      warningLine = null;
    }

    function setHint(message) {
      hintEl.textContent = message;
      hintEl.hidden = !message;
    }

    function showFatalError() {
      clearError();
      addError("We couldn't upload that. Please email your files to ", emailLink());
    }

    function isBusy() {
      return items.some((item) => item.state === 'queued' || item.state === 'uploading');
    }

    function syncField() {
      const done = items.filter((item) => item.state === 'done');
      writeStored(
        storageKey,
        done.length ? done.map(({ name, size, key, url, token }) => ({ name, size, key, url, token })) : null
      );
      if (!done.length) {
        hiddenInput.value = '';
        return;
      }
      const lines = done.map((item) => `${item.name} (${formatSize(item.size)}): ${item.url}`);
      const expiries = done.map((item) => expiryOf(item.url)).filter(Boolean);
      if (expiries.length) {
        const date = new Date(Math.min(...expiries) * 1000).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
        lines.push(`Download by ${date}: links stop working after that and the files are deleted.`);
      }
      hiddenInput.value = lines.join('\n');
    }

    function syncSubmit() {
      if (!submitButton) return;
      const busy = isBusy();
      submitButton.disabled = busy;
      if (busy) submitButton.setAttribute('aria-disabled', 'true');
      else submitButton.removeAttribute('aria-disabled');
      if (submitButton.tagName === 'BUTTON') submitButton.textContent = busy ? 'Uploading files…' : submitLabel;
    }

    function render(item) {
      const status = {
        queued: 'Waiting…',
        uploading: `Uploading ${item.progress}%`,
        done: 'Added',
        error: item.message,
      }[item.state];
      item.statusEl.textContent = status;
      item.row.dataset.state = item.state;
      item.retryButton.hidden = item.state !== 'error';
    }

    function setState(item, state, message = '') {
      resetWarning();
      item.state = state;
      item.message = message;
      render(item);
      syncField();
      syncSubmit();
    }

    function createRow(item) {
      const row = document.createElement('li');
      row.className = 'form-uploads__item';

      const name = document.createElement('span');
      name.className = 'form-uploads__name';
      name.textContent = item.name;

      const meta = document.createElement('span');
      meta.className = 'form-uploads__meta';
      const size = document.createElement('span');
      size.textContent = formatSize(item.size);
      const status = document.createElement('span');
      status.className = 'form-uploads__state';
      meta.append(size, ' · ', status);

      const retry = document.createElement('button');
      retry.type = 'button';
      retry.className = 'form-uploads__action';
      retry.textContent = 'Try again';
      retry.hidden = true;
      retry.addEventListener('click', () => {
        clearError();
        setState(item, 'queued');
        processQueue();
      });

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'form-uploads__remove';
      remove.setAttribute('aria-label', `Remove ${item.name}`);
      remove.textContent = '×';
      remove.addEventListener('click', () => removeItem(item));

      const text = document.createElement('span');
      text.className = 'form-uploads__text';
      text.append(name, meta);
      row.append(text, retry, remove);

      item.row = row;
      item.statusEl = status;
      item.retryButton = retry;
      list.appendChild(row);
    }

    function dropItem(item) {
      resetWarning();
      if (item.xhr) item.xhr.abort();
      if (item.state === 'done' && item.key) {
        fetch(`${endpoint}/upload/${item.key.split('/').map(encodeURIComponent).join('/')}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${item.token}` },
        }).catch(() => {});
      }
      items.splice(items.indexOf(item), 1);
      item.row.remove();
      syncField();
      syncSubmit();
    }

    function removeItem(item) {
      dropItem(item);
      announce(`${item.name} removed`);
      zone.focus();
    }

    function restoreItems() {
      const saved = readStored(storageKey);
      if (!Array.isArray(saved)) return;
      const now = Date.now() / 1000;
      for (const entry of saved.slice(0, MAX_FILES)) {
        const valid = entry && [entry.name, entry.key, entry.url, entry.token].every((value) => typeof value === 'string') && Number.isFinite(entry.size);
        if (!valid || expiryOf(entry.url) <= now) continue;
        const item = { id: nextId++, file: null, name: entry.name, size: entry.size, state: 'done', message: '', progress: 100, xhr: null, key: entry.key, url: entry.url, token: entry.token };
        items.push(item);
        createRow(item);
        render(item);
      }
      syncField();
      syncSubmit();
    }

    function getToken() {
      return loadTurnstile().then(
        (turnstile) =>
          new Promise((resolve, reject) => {
            let timer = setTimeout(() => settle(reject, new Error('Turnstile timed out')), TOKEN_TIMEOUT_MS);
            function settle(fn, value) {
              clearTimeout(timer);
              setHint('');
              pendingToken = null;
              fn(value);
            }
            pendingToken = { resolve: (token) => settle(resolve, token), reject: (error) => settle(reject, error), stopTimer: () => clearTimeout(timer) };

            if (widgetId === null) {
              widgetId = turnstile.render(widgetEl, {
                sitekey: siteKey,
                appearance: 'interaction-only',
                callback: (token) => pendingToken && pendingToken.resolve(token),
                'before-interactive-callback': () => {
                  if (pendingToken) pendingToken.stopTimer();
                  setHint('Please tick the box below to confirm you are human, then your files will upload.');
                },
                'error-callback': (code) => {
                  if (pendingToken) pendingToken.reject(new Error(`Turnstile error ${code}`));
                  return true;
                },
              });
            } else {
              turnstile.reset(widgetId);
            }
          })
      );
    }

    async function createSession() {
      try {
        const token = await getToken();
        const response = await fetch(`${endpoint}/session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });
        if (!response.ok) throw new Error(`Session request failed (${response.status})`);
        const data = await response.json();
        if (!data.session || !data.expires) throw new Error('Session response was incomplete');
        session = { token: data.session, expires: data.expires };
      } catch (error) {
        session = null;
        throw new SessionError(error.message);
      }
    }

    async function getSession(forceNew = false) {
      const now = Date.now() / 1000;
      if (!forceNew && session && session.expires - SESSION_MARGIN_SECONDS > now) return session.token;
      if (!sessionRequest) sessionRequest = createSession().finally(() => (sessionRequest = null));
      await sessionRequest;
      return session.token;
    }

    function send(item, token) {
      return new Promise((resolve) => {
        const xhr = new XMLHttpRequest();
        item.xhr = xhr;
        xhr.open('POST', `${endpoint}/upload`);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.setRequestHeader('X-File-Name', encodeURIComponent(item.file.name));
        xhr.setRequestHeader('Content-Type', 'application/octet-stream');
        xhr.upload.onprogress = (event) => {
          if (!event.lengthComputable) return;
          item.progress = Math.min(99, Math.round((event.loaded / event.total) * 100));
          render(item);
        };
        xhr.onload = () => {
          let body = {};
          try {
            body = JSON.parse(xhr.responseText);
          } catch {}
          resolve({ status: xhr.status, body });
        };
        xhr.onerror = () => resolve({ status: 0, body: {} });
        xhr.onabort = () => resolve({ status: -1, body: {} });
        xhr.send(item.file);
      });
    }

    function failureMessage(result) {
      if (result.status === 413) return `Too large (${MAX_MB} MB maximum)`;
      if (result.status === 415) return "This file type isn't supported";
      if (result.status === 429) return 'Too many files (10 maximum)';
      return result.body.error || 'Upload failed';
    }

    async function uploadItem(item) {
      item.progress = 0;
      setState(item, 'uploading');
      try {
        let token = await getSession();
        if (!items.includes(item)) return;
        let result = await send(item, token);
        if (result.status === -1) return;
        if (result.status === 401) {
          token = await getSession(true);
          if (!items.includes(item)) return;
          result = await send(item, token);
          if (result.status === -1) return;
        }
        if (result.status === 201) {
          item.key = result.body.key;
          item.url = result.body.url;
          item.token = token;
          setState(item, 'done');
          announce(`${item.name} added`);
          return;
        }
        if (result.status === 0 || result.status >= 500) showFatalError();
        setState(item, 'error', failureMessage(result));
      } catch (error) {
        if (!(error instanceof SessionError)) throw error;
        showFatalError();
        for (const other of items) if (other.state === 'queued') setState(other, 'error', "Couldn't upload");
        setState(item, 'error', "Couldn't upload");
      } finally {
        item.xhr = null;
      }
      if (item.state === 'error') announce(`${item.name} failed: ${item.message}`);
    }

    async function processQueue() {
      if (processing) return;
      processing = true;
      try {
        let next;
        while ((next = items.find((item) => item.state === 'queued'))) await uploadItem(next);
      } finally {
        processing = false;
      }
    }

    function addFiles(fileList) {
      clearError();
      resetWarning();
      let active = items.filter((item) => item.state !== 'error').length;
      for (const file of Array.from(fileList)) {
        if (!EXTENSIONS.includes(extensionOf(file.name))) {
          rejectedFile = true;
          addError(`${file.name}: this file type isn't supported. Please send images, PDF, AI, EPS, CDR or Word files.`);
        } else if (file.size === 0) {
          rejectedFile = true;
          addError(`${file.name}: the file is empty.`);
        } else if (file.size > MAX_BYTES) {
          rejectedFile = true;
          addError(
            `${file.name}: too large (${formatSize(file.size)}). The limit is ${MAX_MB} MB per file. Please attach a smaller version, put a WeTransfer or Dropbox link in your message, or email it to `,
            emailLink()
          );
        } else if (active >= MAX_FILES) {
          rejectedFile = true;
          addError(`${file.name}: not added, you can attach up to ${MAX_FILES} files.`);
        } else {
          const item = { id: nextId++, file, name: file.name, size: file.size, state: 'queued', message: '', progress: 0, xhr: null, key: null, url: null, token: null };
          items.push(item);
          createRow(item);
          render(item);
          active += 1;
        }
      }
      syncSubmit();
      processQueue();
    }

    zone.addEventListener('click', () => fileInput.click());
    fileInput.addEventListener('change', () => {
      if (fileInput.files.length) addFiles(fileInput.files);
      fileInput.value = '';
    });

    ['dragenter', 'dragover'].forEach((type) =>
      form.addEventListener(type, (event) => {
        if (!hasFiles(event)) return;
        event.preventDefault();
        zone.classList.add('is-dragover');
      })
    );
    form.addEventListener('dragleave', (event) => {
      if (!form.contains(event.relatedTarget)) zone.classList.remove('is-dragover');
    });
    form.addEventListener('dragend', () => zone.classList.remove('is-dragover'));
    form.addEventListener('drop', (event) => {
      if (!hasFiles(event)) return;
      event.preventDefault();
      zone.classList.remove('is-dragover');
      addFiles(event.dataTransfer.files);
    });

    form.addEventListener('form-uploads:clear', () => {
      [...items].forEach(dropItem);
      clearError();
      setHint('');
      rejectedFile = false;
    });

    function sendWarning() {
      const failed = items.filter((item) => item.state === 'error').length;
      if (failed) {
        const [subject, pronoun] = failed === 1 ? ['1 file', 'it'] : [`${failed} files`, 'them'];
        return `${subject} didn't upload. Try again or remove ${pronoun}, or press Send again to send without ${pronoun}.`;
      }
      if (items.some((item) => item.state === 'done')) return '';
      const textarea = form.querySelector('textarea');
      if (textarea && MENTIONS_ATTACHMENT.test(textarea.value)) {
        return 'Your message mentions an attachment but no file is attached. Add it above, or press Send again to send without it.';
      }
      if (rejectedFile) return "Your file wasn't added. Try another above, or press Send again to send without it.";
      return '';
    }

    form.addEventListener(
      'submit',
      (event) => {
        if (isBusy()) {
          event.preventDefault();
          event.stopImmediatePropagation();
          announce('Please wait for your files to finish uploading');
          return;
        }
        const warning = warned ? '' : sendWarning();
        if (!warning) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        warned = true;
        warningLine = addError(warning);
        zone.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
        zone.focus({ preventScroll: true });
      },
      true
    );

    if (form.querySelector('[data-contact-posted]')) writeStored(storageKey, null);
    else restoreItems();
  }

  document.querySelectorAll('[data-form-uploads]').forEach(init);
})();
